/* eslint-disable react-refresh/only-export-components */
import Cookies from 'js-cookie'
import type React from 'react'
import { createContext, useContext, useEffect, useState } from 'react'
import type { Units, UnitsContextType, UnitsProviderProps } from '@/types/contexts'

export type { Units }

const UnitsContext = createContext<UnitsContextType | undefined>(undefined)

const getUserPreference = (): Units | null => {
  const preference = Cookies.get('user_units_preference')
  if (preference === 'm' || preference === 'i') {
    return preference
  }
  return null
}

const saveUserPreference = (units: Units) => {
  Cookies.set('user_units_preference', units, {
    expires: 365, // 1 year
    path: '/',
    secure: window.location.protocol === 'https:',
    sameSite: 'strict',
  })
}

const getDetectedUnits = (): Units | null => {
  if (typeof window === 'undefined') return null
  const detected = sessionStorage.getItem('detected_units')
  if (detected === 'm' || detected === 'i') {
    return detected
  }
  return null
}

const saveDetectedUnits = (units: Units) => {
  if (typeof window === 'undefined') return
  sessionStorage.setItem('detected_units', units)
}

export const UnitsProvider: React.FC<UnitsProviderProps> = ({ children }) => {
  const [units, setUnitsState] = useState<Units>('m') // Default: metric
  const [isDetecting, setIsDetecting] = useState<boolean>(true)

  useEffect(() => {
    const initializeUnits = async () => {
      // Priority 1: User preference (cookie) - has highest priority
      const userPreference = getUserPreference()
      if (userPreference) {
        setUnitsState(userPreference)
        setIsDetecting(false)
        return
      }

      // Priority 2: Already detected in this session (sessionStorage)
      const detectedUnits = getDetectedUnits()
      if (detectedUnits) {
        setUnitsState(detectedUnits)
        setIsDetecting(false)
        return
      }

      // Priority 3: Auto-detect from location
      try {
        const response = await fetch('https://ipapi.co/json/')
        const data = await response.json()
        const detected: Units = data.country_code === 'US' ? 'i' : 'm'
        saveDetectedUnits(detected)
        setUnitsState(detected)
      } catch (error) {
        console.error('Failed to detect location, using metric as default:', error)
        // Fallback to metric
        saveDetectedUnits('m')
        setUnitsState('m')
      } finally {
        setIsDetecting(false)
      }
    }

    initializeUnits()
  }, [])

  const setUnits = (newUnits: Units) => {
    setUnitsState(newUnits)
    // Save user preference (365 days) - this will override auto-detection
    saveUserPreference(newUnits)
  }

  return (
    <UnitsContext.Provider
      value={{
        units,
        setUnits,
        isDetecting,
      }}
    >
      {children}
    </UnitsContext.Provider>
  )
}

export const useUnits = () => {
  const context = useContext(UnitsContext)
  if (context === undefined) {
    throw new Error('useUnits must be used within a UnitsProvider')
  }
  return context
}
